import { z } from 'zod';
import * as zfd from 'zod-form-data';

import type {
    ActionFunctionArgs,
    LoaderFunctionArgs,
} from '~/types/route-args';
import { getDigitalPublicationOptions } from '@modules-rocco/subscription/api/get-digital-publication-options.server';
import { placeholderDigitalPublications } from '@modules-rocco/subscription/config/placeholder-publications';
import { getMemberSubscribeLocation } from '@modules-rocco/subscription/api/get-member-subscribe-location.server';
import {
    subscribeToDigitalPublications,
    type SubscribeToPublicationsResult,
} from '@modules-rocco/subscription/api/subscribe-to-digital-publications.server';
import { isSubscriberType } from '@modules-rocco/subscription/subscriber-type';
import type { SubscriberTypeEnum } from 'generated/graphql';

/** Publication catalogue for the subscribe modal, and the subscribe itself. */
export const SUBSCRIPTION_PUBLICATIONS_API_ROUTE =
    '/remix-api/subscription/publications';

export const loader = async ({ context }: LoaderFunctionArgs) => {
    const [catalogue, memberLocation] = await Promise.all([
        getDigitalPublicationOptions(context),
        // Null for a visitor, or a member who has never set one — the field then
        // behaves as it did before.
        getMemberSubscribeLocation(context),
    ]);

    // Outside production an empty catalogue falls back to placeholders, for the
    // same reason the articles publications row does: no environment but
    // production has publications configured, so the gate would otherwise open
    // on an empty pane and nothing about it could be reviewed. Production shows
    // only what the CMS holds.
    const options =
        catalogue.length === 0
            ? placeholderDigitalPublications(context.env.stage)
            : catalogue;

    return { options, memberLocation };
};

const subscribeSchema = zfd.formData({
    email: zfd.text(
        z
            .string()
            .min(1, { message: 'Email address is required' })
            .email('This is not a valid email address'),
    ),
    // One checkbox per publication, so the browser sends the field repeatedly.
    hubspotIDs: zfd.repeatable(
        z
            .array(zfd.text())
            .min(1, 'Choose at least one publication to subscribe to.'),
    ),
    name: zfd.text(z.string().optional()),
    subscriberType: zfd.text(z.string().optional()),
    // The parts of a picked place. The server maps these onto HubSpot's dropdown
    // options and drops anything it has no option for.
    region: zfd.text(z.string().optional()),
    country: zfd.text(z.string().optional()),
});

export const action = async ({
    request,
    context,
}: ActionFunctionArgs): Promise<SubscribeToPublicationsResult> => {
    const parsed = subscribeSchema.safeParse(await request.formData());

    if (!parsed.success) {
        const issue = parsed.error.issues[0];
        return {
            success: false,
            message: issue?.message ?? 'Please check the form and try again.',
            // The zod path names the field, so the modal can mark the input the
            // server would have named had the request got that far.
            field: issue?.path?.[0] === 'hubspotIDs' ? 'HubspotIDs' : 'Email',
        };
    }

    const { email, hubspotIDs, name, subscriberType, region, country } =
        parsed.data;

    return subscribeToDigitalPublications(
        {
            Email: email,
            HubspotIDs: hubspotIDs,
            Name: name || undefined,
            // A value the schema does not hold would fail GraphQL validation and
            // take the whole subscribe with it, so an unrecognised one is dropped
            // rather than forwarded.
            SubscriberType: isSubscriberType(subscriberType ?? null)
                ? (subscriberType as SubscriberTypeEnum)
                : undefined,
            Region: region || undefined,
            Country: country || undefined,
        },
        context,
    );
};
